/**
 * Remonter en haut de page.
 *
 * Plusieurs écrans sont longs — l'accès rapide, les guides, un répertoire de
 * trois cents adhérents — et l'on y descend pour chercher une chose précise.
 * Remonter demandait ensuite un balayage de pouce interminable, ou de fermer
 * la page pour la rouvrir.
 *
 * Ce fichier porte la SEULE mécanique de remontée de l'application. Le bouton
 * flottant et celui du bas des guides appellent tous deux remonterEnHaut() :
 * une seconde implémentation écrite sur place se serait mise à diverger, et
 * c'est exactement ce qui était arrivé la première fois.
 *
 * Elle vit dans un module à elle, et non plus à côté du composant du bouton :
 * un fichier qui exporte un composant ET des fonctions casse le
 * rafraîchissement à chaud du rendu, et le contrôle du dépôt le refusait.
 *
 * CE QUE DEUX VERSIONS PRÉCÉDENTES ONT CRU À TORT
 * ---------------------------------------------------------------------------
 * La première appelait window.scrollTo, en supposant que la fenêtre défile. La
 * seconde a supposé l'inverse — que le défilement vit dans un conteneur interne
 * — et retenait « celui qui déborde le plus » parmi les .overflow-y-auto.
 *
 * Mesuré au navigateur, sur les deux mises en page : AUCUN de ces conteneurs ne
 * déborde. Le document, lui, déborde de 2 792 pixels sur grand écran et de
 * 6 657 sur téléphone. C'est donc bien la fenêtre qui défile.
 *
 * Le seul .overflow-y-auto qui déborde parfois est le MENU LATÉRAL, quand il a
 * plus d'entrées que de hauteur. La règle du plus grand débordement le
 * désignait alors : le bouton remontait le menu, la page ne bougeait pas — et
 * comme le défilement était guetté sur le menu, la flèche ne paraissait jamais
 * non plus.
 *
 * COMMENT ON S'Y PREND MAINTENANT
 * ---------------------------------------------------------------------------
 * On ne devine plus : on remonte les ANCÊTRES du point d'où part la demande. Le
 * premier qui défile pour de bon est celui qui porte le contenu qu'on lit — et
 * le menu latéral n'est l'ancêtre de personne. Sans point de départ, c'est la
 * fenêtre, dès lors que le document déborde.
 */

/** Cet élément défile-t-il pour de bon ? La classe ne suffit pas à le dire. */
function defileVraiment(element: HTMLElement): boolean {
  if (element.scrollHeight <= element.clientHeight) return false;

  const sens = getComputedStyle(element).overflowY;

  return sens === 'auto' || sens === 'scroll' || sens === 'overlay';
}

/**
 * Ce qui défile, vu depuis un point de la page.
 *
 * @param depuis un élément du contenu — le bouton cliqué, par exemple. Ses
 *   ancêtres sont remontés jusqu'au premier qui défile. Omis, on retient la
 *   fenêtre dès lors que le document déborde.
 */
export function conteneurQuiDefile(depuis?: Element | null): HTMLElement | Window {
  for (let noeud = depuis?.parentElement ?? null; noeud; noeud = noeud.parentElement) {
    if (defileVraiment(noeud)) return noeud;
  }

  const doc = document.documentElement;
  if (doc.scrollHeight > doc.clientHeight) return window;

  /*
   * Le document ne déborde pas : ou la page est courte, ou la mise en page a
   * changé et confie de nouveau le défilement à un conteneur. On cherche alors
   * dans le contenu — jamais dans le menu, qui est hors de <main>.
   */
  const contenu = Array.from(document.querySelectorAll('main')).find((m) => m.offsetParent !== null);
  const interne = contenu
    ? Array.from(contenu.querySelectorAll<HTMLElement>('*')).find(defileVraiment)
    : undefined;

  return interne ?? window;
}

/**
 * Remonte en haut. L'unique façon de le faire dans cette application.
 *
 * @param depuis d'où part la demande, pour retrouver ce qui défile. Le bouton
 *   du bas d'un guide passe le sien ; le bouton flottant, posé en position
 *   fixe, n'a pas d'ancêtre utile et n'en passe pas.
 */
export function remonterEnHaut(depuis?: Element | null) {
  // Le mouvement animé est agréable, mais il donne le vertige à qui a demandé
  // qu'on le lui épargne. Le réglage du système fait foi.
  const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  conteneurQuiDefile(depuis).scrollTo({ top: 0, behavior: doux ? 'smooth' : 'auto' });
}
